import type { Editor, JSONContent } from "@tiptap/react";
import { blockIdAtPos } from "@repo/shared/blockIdAtPos";
import {
  chapterContentChanged,
  chapterContentToPlainText,
  plainTextToChapterDoc,
  serializeChapterContent,
  stripCommentOverlayMarksFromJson,
  toChapterDoc,
  type DocContent,
} from "../../shared/chapterContent";
import {
  ensureParagraphIds,
  getParagraphById,
  getParagraphByIndex,
  listChapterParagraphs,
  type ChapterDocContent,
  type ChapterParagraphRef,
} from "../../shared/chapterParagraphs";
import type { Chapter } from "../types";
import { reconcileChapterCommentsWithEditor } from "./chapterCommentAnchorSync";
import { reapplyChapterCommentMarks } from "./commentEditorMarks";

export {
  chapterContentToPlainText,
  getParagraphById,
  getParagraphByIndex,
  listChapterParagraphs,
  type ChapterParagraphRef,
};

/** @deprecated Use chapterContentToPlainText — kept for call sites that expect the old name. */
export function normalizeChapterContent(value: unknown): string {
  return chapterContentToPlainText(value);
}

/** Plain text for editor ↔ chapter sync comparisons (legacy name). */
export function chapterPlainTextForSync(value: unknown): string {
  return chapterContentToPlainText(value).replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();
}

/** Plain text for chapter variation diffs — keeps paragraph breaks (`\n\n`). */
export function chapterPlainTextForDiff(value: unknown): string {
  return chapterContentToPlainText(value);
}

export function manuscriptJsonFromEditor(editor: Editor): string {
  try {
    const json = editor.getJSON() as JSONContent;
    const doc = ensureParagraphIds(json as ChapterDocContent);
    const stripped = stripCommentOverlayMarksFromJson(doc);
    return JSON.stringify(stripped);
  } catch {
    return serializeChapterContent("");
  }
}

export function chapterToTipTapDoc(value: unknown): DocContent {
  return toChapterDoc(value).doc;
}

/** Compare chapter bodies using stable TipTap JSON fingerprints. */
export function chapterContentBodyChanged(before: unknown, after: unknown): boolean {
  return chapterContentChanged(before, after);
}

/** Reconcile comment marks without replacing manuscript body (preserves selection and scroll). */
export function reconcileManuscriptEditorComments(
  editor: Editor,
  comments: Chapter["comments"],
  appliedCommentMarkIds: Set<string>
): Chapter["comments"] {
  const reconciled = reconcileChapterCommentsWithEditor(editor, comments);
  reapplyChapterCommentMarks(editor, reconciled, appliedCommentMarkIds);
  return reconciled;
}

/** Load chapter manuscript into TipTap (resets undo stack — only on chapter switch / external reload). */
export function hydrateManuscriptEditor(
  editor: Editor,
  chapter: Pick<Chapter, "content" | "comments">,
  appliedCommentMarkIds: Set<string>
): Chapter["comments"] {
  const plain = chapterContentToPlainText(chapter.content).trim();
  const incomingJson = serializeChapterContent(chapter.content);
  const currentJson = manuscriptJsonFromEditor(editor);
  const editorEmpty = editor.state.doc.textContent.trim().length === 0;
  if (currentJson === incomingJson && !(editorEmpty && plain.length > 0)) {
    return reconcileManuscriptEditorComments(editor, chapter.comments, appliedCommentMarkIds);
  }

  const { doc, hasText } = toChapterDoc(chapter.content);
  const selectionFrom = Math.min(editor.state.selection.from, editor.state.doc.content.size);
  const selectionTo = Math.min(Math.max(editor.state.selection.to, selectionFrom), editor.state.doc.content.size);
  const scrollEl =
    typeof editor.view?.dom?.closest === "function"
      ? (editor.view.dom.closest(".manuscript-surface") as HTMLElement | null)
      : null;
  const scrollTop = scrollEl?.scrollTop ?? 0;

  try {
    editor.commands.setContent(doc as JSONContent, { emitUpdate: false });
  } catch (error) {
    console.warn("[manuscript] setContent failed; falling back to plain text", error);
    editor.commands.setContent(plainTextToChapterDoc(plain) as JSONContent, { emitUpdate: false });
  }

  // TipTap can accept JSON and still leave an empty doc (schema/attr mismatches). Recover.
  if (editor.state.doc.textContent.trim().length === 0 && plain.length > 0) {
    console.warn("[manuscript] editor empty after JSON hydrate; applying plain-text fallback");
    editor.commands.setContent(plainTextToChapterDoc(plain) as JSONContent, { emitUpdate: false });
  } else if (!hasText && plain.length > 0) {
    editor.commands.setContent(plainTextToChapterDoc(plain) as JSONContent, { emitUpdate: false });
  }

  const newSize = editor.state.doc.content.size;
  if (newSize > 1) {
    const from = Math.min(selectionFrom, newSize);
    const to = Math.min(Math.max(selectionTo, from), newSize);
    try {
      editor.commands.setTextSelection({ from, to });
    } catch {
      /* selection restore is best-effort */
    }
  }
  if (scrollEl) {
    scrollEl.scrollTop = scrollTop;
    requestAnimationFrame(() => {
      scrollEl.scrollTop = scrollTop;
    });
  }

  return reconcileManuscriptEditorComments(editor, chapter.comments, appliedCommentMarkIds);
}

export function buildCommentAnchorFromSelection(editor: Editor | null): Chapter["comments"][number]["anchor"] {
  if (!editor) return undefined;
  const docSize = editor.state.doc.content.size;
  if (docSize <= 1) return undefined;
  const rawFrom = editor.state.selection.from;
  const rawTo = editor.state.selection.to;
  const from = Math.max(0, Math.min(rawFrom, docSize));
  const to = Math.max(from, Math.min(rawTo, docSize));
  if (to <= from) return undefined;
  let excerpt = "";
  try {
    excerpt = editor.state.doc.textBetween(from, to, " ", " ");
  } catch {
    excerpt = "";
  }
  excerpt = excerpt.replace(/\s+/g, " ").trim().slice(0, 180);
  return { from, to, excerpt: excerpt || undefined };
}

/** Capture selection for the in-doc comment composer (anchor + blockId) before focus moves. */
export function captureCommentSelection(editor: Editor | null): {
  anchor: NonNullable<Chapter["comments"][number]["anchor"]>;
  blockId: string | null;
} | null {
  if (!editor) return null;
  const anchor = buildCommentAnchorFromSelection(editor);
  if (!anchor || !(anchor.to > anchor.from)) return null;
  const blockId = blockIdAtPos(editor.state.doc, anchor.from);
  return { anchor, blockId };
}

/** @deprecated Use chapterToTipTapDoc — HTML hydration replaced by TipTap JSON. */
export function chapterToTipTapHtml(value: unknown): string {
  const doc = chapterToTipTapDoc(value);
  const paragraphs = listChapterParagraphs(doc);
  if (!paragraphs.length) return "<p></p>";
  return paragraphs
    .map((p) => `<p data-paragraph-id="${p.id}">${escapeHtml(p.text)}</p>`)
    .join("");
}

/** @deprecated Use manuscriptJsonFromEditor */
export function manuscriptPlainFromEditor(editor: Editor): string {
  return chapterContentToPlainText(manuscriptJsonFromEditor(editor));
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
